import axios from 'axios';
import moment from 'moment';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';
import { useSelector, useDispatch } from 'react-redux';
import { TextField, Checkbox } from '@mui/material';
import React, { useEffect, useCallback, useRef, useState } from "react";
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalBody, ModalHeader } from 'reactstrap';
import ReactDOM from "react-dom";
import Draggable from "react-draggable";


import CreateRelation from './prop/CreateRelation';
import PendingAttachmentsList from '../Attachments/PendingAttachmentsList';
import { updateActivityFormField, toggleDropdown as toggleDropdownAction } from '../../store/Activities';

const AddNote = ({ clearActivityForm, onCreateActivity, isActiveExpande, setIsActiveExpande }) => {

   const dispatch = useDispatch();
   const dropdownWrapperRef = useRef(null);
   const quillRef = useRef(null);

   const [activeDrags, setActiveDrags] = useState(0);
   const [modalDroppedAlert, setModalDroppedAlert] = useState(false);
   const [deltaPosition, setDeltaPosition] = useState({ x: 0, y: 0 });
   const [controlledPosition, setControlledPosition] = useState({ x: -400, y: 200 });

   let { associations, loading, activityForm, dropdowns, activeActivityBox } = useSelector(state => state['Activities']);
   // Use local state for pending attachments to avoid cross-component sharing
   const [localPendingAttachments, setLocalPendingAttachments] = useState([]);
   const fileInputRef = useRef(null);

   const [isMobile, setIsMobile] = useState(false);

   useEffect(() => {
      const handleResize = () => {
         setIsMobile(window.matchMedia("(max-width: 991px)").matches);
      };

      handleResize(); // Set initial state
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   // Initialize form dates on mount
   useEffect(() => {
      if (!activityForm['dueDate']) dispatch(updateActivityFormField('dueDate', moment().add(2, 'days').format('YYYY-MM-DD')));
      if (!activityForm['followUpDate']) dispatch(updateActivityFormField('followUpDate', moment().add(2, 'days').format('YYYY-MM-DD')));
   }, [dispatch, activityForm['dueDate'], activityForm['followUpDate']]);

   // Focus on ReactQuill editor when component mounts
   useEffect(() => {
      if (quillRef.current) {
         const editor = quillRef.current.getEditor();
         editor.focus();
      }
   }, []);

   // Update form field helper
   const updateFormField = useCallback((field, value) => { dispatch(updateActivityFormField(field, value)); }, [dispatch]);

   // Toggle dropdown helper
   const toggleDropdown = useCallback((dropdownName) => { dispatch(toggleDropdownAction(dropdownName)); }, [dispatch]);

   // Handle create note
   const handleCreateNote = () => {
      if (!activityForm['valueRichText']) return;
      const activityData = {
         type: 'note',
         status: 'completed',
         completedAt: new Date(),
         associations, // Pass associations object directly
         content: activityForm['valueRichText'],
         description: activityForm['valueRichText'],
         title: activityForm['valueRichText'].replace(/<[^>]*>/g, '').trim().substring(0, 50) + (activityForm['valueRichText'].replace(/<[^>]*>/g, '').trim().length > 50 ? '...' : '') || 'Note',
      };

      // Include attachments if any
      if (localPendingAttachments && localPendingAttachments.length > 0) {
         activityData['attachments'] = localPendingAttachments.map(file => file['file']);
      }

      // If follow-up task is checked, include task data
      if (activityForm['createFollowUp']) {
         activityData['createFollowUp'] = {
            type: 'task',
            taskType: activityForm['followUpTaskType'],
            dueDate: moment(activityForm['followUpDate']).hour(12).toISOString()
         };
      }

      onCreateActivity(activityData);
      // Clear local attachments after creating
      setLocalPendingAttachments([]);
   };

   // Handlers for local attachment management
   const handleFilesAdded = (newFiles) => {
      setLocalPendingAttachments(prev => [...prev, ...newFiles]);
   };

   const handleFileRemoved = (fileId) => {
      setLocalPendingAttachments(prev => prev.filter(f => f['id'] !== fileId));
   };

   const handleToolbarFileSelect = (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      const maxSize = 100 * 1024 * 1024;
      const maxFiles = 5;
      const validFiles = files.filter(file => file['size'] <= maxSize && file['size'] > 0);
      if (localPendingAttachments.length + validFiles.length > maxFiles) { alert(`Maximum ${maxFiles} files allowed.`); return; }
      const newFiles = validFiles.map(file => ({ id: Date.now() + '_' + Math.random().toString(36).substr(2, 9) + '_' + file['name'], name: file['name'], size: file['size'], type: file['type'] || '', lastModified: file['lastModified'], file: file }));
      setLocalPendingAttachments(prev => [...prev, ...newFiles]);
      e.target.value = '';
   };

   // ✅ Event Logger for Draggable
   const handleDrag = useCallback((e, ui) => {
      setDeltaPosition(prev => ({
         x: prev.x + ui.deltaX,
         y: prev.y + ui.deltaY,
      }));
   }, []);

   const onStart = () => setActiveDrags(d => d + 1);
   const onStop = () => setActiveDrags(d => d - 1);

   const onDrop = (e) => {
      setActiveDrags(d => d - 1);
      if (e.target.classList.contains("drop-target")) {
         setModalDroppedAlert(false);
         e.target.classList.remove("hovered");
      }
   };

   const onDropAreaMouseEnter = (e) => {
      if (activeDrags) e.target.classList.add("hovered");
   };
   const onDropAreaMouseLeave = (e) => {
      e.target.classList.remove("hovered");
   };

   const adjustXPos = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setControlledPosition(prev => ({ x: prev.x - 10, y: prev.y }));
   };

   const adjustYPos = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setControlledPosition(prev => ({ x: prev.x, y: prev.y - 10 }));
   };

   const onControlledDrag = (e, position) => {
      const { x, y } = position;
      setControlledPosition({ x, y });
   };

   const onControlledDragStop = (e, position) => {
      onControlledDrag(e, position);
      onStop();
   };

   const dragHandlers = { onStart, onStop };

   const editorModules = { toolbar: '#custom-toolbar', clipboard: { matchVisual: false } };
   const formats = ['font', 'size', 'color', 'background', 'align', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'link', 'image', 'align', 'attachment'];

   return (
      <>
         <Draggable
            {...dragHandlers}
            disabled={isMobile}
            bounds="body" // ✅ allow dragging across the full page
            handle=".add-note-header" // optional: drag only by header
         >
            <div className={`add-note-sec note-single-box ${isActiveExpande ? 'expande-box' : ''}`}>
               <div className="add-note-box">
                  <div className="add-note-header" >
                     <div className="left-area">
                        <h4>Add Note</h4>
                     </div>
                     <div className="right-area">
                        <button className="close-btns" onClick={() => setIsActiveExpande(prev => !prev)}>
                           <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.45508 7.9541L2.69886 11.8543L6.59902 11.6105" stroke="var(--primary-color)" stroke-width="1.11421" stroke-linecap="round" stroke-linejoin="round" />
                              <path d="M7.64551 2.07227L11.5457 1.82855L11.7895 5.72871" stroke="var(--primary-color)" stroke-width="1.11421" stroke-linecap="round" stroke-linejoin="round" />
                              <path d="M11.5444 1.82812L2.69819 11.8538" stroke="var(--primary-color)" stroke-width="1.11421" stroke-linecap="round" stroke-linejoin="round" />
                           </svg>
                        </button>
                        <button className="close-btns" onClick={(e) => { e.stopPropagation(); clearActivityForm(); setIsActiveExpande(false); }}>
                           <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <mask id="path-1-inside-1_1359_1884" fill="white">
                                 <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" />
                              </mask>
                              <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                              <path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1359_1884)" />
                           </svg>
                        </button>
                     </div>
                  </div>
                  <div className="bottom-note-box">
                     {/* Task/Note Description */}
                     <div className="group-form">
                        <label class="text-capitalize">{activeActivityBox} description</label>
                        <div className="editor-wrapper">
                           <ReactQuill
                              ref={quillRef}
                              formats={formats}
                              modules={editorModules}
                              bounds=".editor-wrapper"
                              theme="snow" // must be snow for custom toolbar
                              value={activityForm['valueRichText']}
                              onChange={(value) => updateFormField('valueRichText', value)}
                              placeholder="Start typing to leave a note..."
                           />

                           {/* Custom Toolbar - MUST BE visible */}
                           <div id="custom-toolbar" className="bottom-toolbar">
                              <span className="ql-formats">
                                 <button className="ql-bold" />
                                 <button className="ql-italic" />
                                 <button className="ql-underline" />
                                 <button className="ql-strike" />

                              </span>
                              <div className="ql-formats more-dropdown">
                                 <button className="more-toggle" onClick={() => toggleDropdown('showMore')} >More ▾</button>
                                 <span className="more-dropdown-content" onClick={(e) => e.stopPropagation()} style={{ display: dropdowns.showMore ? 'block' : 'none' }}>
                                    <svg data-test-id="popover-arrow" fill="#FFFFFF" aria-hidden="true" width="30" height="28" viewBox="0 0 28 28" style={{ position: 'absolute', pointerEvents: 'none', top: '100%', left: '7.7185px' }}><path clip-path="url(#:ra5:)" fill="none" stroke="#CBD6E2" stroke-width="3" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><path stroke="#FFFFFF" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><clipPath id=":ra5:"><rect x="-1" y="1" width="30" height="28"></rect></clipPath></svg>
                                    <span className="ql-formats" onClick={(e) => e.stopPropagation()}>
                                       <select className="ql-font" />
                                       <select className="ql-size" />
                                       <select className="ql-color" />
                                       <select className="ql-background" />
                                       <button className="ql-blockquote" />
                                       <button className="ql-list" value="ordered" />
                                       <select className="ql-align" />
                                    </span>
                                 </span>
                              </div>
                              <span className="ql-formats">
                                 <button className="ql-list" value="ordered" />
                                 <button className="ql-list" value="bullet" />
                              </span>
                              <span className="ql-formats">
                                 <button className="ql-link" />
                                 <button className="ql-image" />
                              </span>
                              <span className="ql-formats">
                                 <button type="button" className="ql-attachment" onClick={() => fileInputRef.current?.click()}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                       <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04ZM14 13V17H10V13H7L12 8L17 13H14Z" fill="currentColor"/>
                                    </svg>
                                 </button>
                                 <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleToolbarFileSelect} />
                              </span>
                           </div>
                        </div>

                     </div>
                     {/* Related with object */}
                     <CreateRelation
                        type="new"
                        initialAssociations={associations}
                     />

                     {/* Attachments List */}
                     <PendingAttachmentsList files={localPendingAttachments} onRemove={handleFileRemoved} />

                     {/* Create a task as well */}
                     {/* <div className="group-form checkbox-group" ref={dropdownWrapperRef}>
                  <label>
                     <Checkbox checked={activityForm?.['createFollowUp']} onChange={(e) => updateFormField('createFollowUp', e.target.checked)}
                     />
                     Create a
                     <Dropdown isOpen={dropdowns.dropdownOpenTODO} toggle={() => toggleDropdown('dropdownOpenTODO')} >
                        <DropdownToggle caret color="secondary">
                           {activityForm.followUpTaskType === 'to-do' ? 'To-do' : activityForm.followUpTaskType.charAt(0).toUpperCase() + activityForm.followUpTaskType.slice(1)}
                           <i className="icon">
                              <svg width="5" height="4" viewBox="0 0 5 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                 <path d="M3.74219 2.40453L2.38875 0L0.385372 0C0.0425479 0 -0.128864 0.552327 0.113969 0.876105L1.96379 3.34253C2.26019 3.73773 2.74229 3.73773 3.03868 3.34253L3.74219 2.40453Z" fill="var(--primary-color)" />
                                 <path d="M4.61365 0L2.38887 0L3.74231 2.40453L4.88863 0.876105C5.12789 0.552327 4.95648 0 4.61365 0Z" fill="var(--primary-color)" />
                              </svg>
                           </i>
                        </DropdownToggle>
                        <DropdownMenu>
                           <DropdownItem onClick={() => updateFormField('followUpTaskType', 'call')}>Call</DropdownItem>
                           <DropdownItem onClick={() => updateFormField('followUpTaskType', 'email')}>Email</DropdownItem>
                           <DropdownItem onClick={() => updateFormField('followUpTaskType', 'to-do')}>To-do</DropdownItem>
                        </DropdownMenu>
                     </Dropdown>
                     task to follow-up
                     <Dropdown isOpen={dropdowns.dropdownOpenDays} toggle={() => toggleDropdown('dropdownOpenDays')}>
                        <DropdownToggle caret color="secondary">
                           {moment(activityForm.followUpDate).calendar(null, { sameDay: '[Today]', nextDay: '[Tomorrow]', nextWeek: 'dddd', sameElse: 'MMM D' })}
                           <i className="icon">
                              <svg width="5" height="4" viewBox="0 0 5 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                 <path d="M3.74219 2.40453L2.38875 0L0.385372 0C0.0425479 0 -0.128864 0.552327 0.113969 0.876105L1.96379 3.34253C2.26019 3.73773 2.74229 3.73773 3.03868 3.34253L3.74219 2.40453Z" fill="var(--primary-color)" />
                                 <path d="M4.61365 0L2.38887 0L3.74231 2.40453L4.88863 0.876105C5.12789 0.552327 4.95648 0 4.61365 0Z" fill="var(--primary-color)" />
                              </svg>
                           </i>
                        </DropdownToggle>
                        <DropdownMenu>
                           <DropdownItem onClick={() => updateFormField('followUpDate', moment().format('YYYY-MM-DD'))}>Today</DropdownItem>
                           <DropdownItem onClick={() => updateFormField('followUpDate', moment().add(1, 'days').format('YYYY-MM-DD'))}>Tomorrow</DropdownItem>
                           <DropdownItem onClick={() => updateFormField('followUpDate', moment().add(2, 'days').format('YYYY-MM-DD'))}>In 2 days</DropdownItem>
                           <DropdownItem onClick={() => updateFormField('followUpDate', moment().add(3, 'days').format('YYYY-MM-DD'))}>In 3 days</DropdownItem>
                           <DropdownItem onClick={() => updateFormField('followUpDate', moment().add(7, 'days').format('YYYY-MM-DD'))}>Next week</DropdownItem>
                           <DropdownItem onClick={() => updateFormField('followUpDate', moment().add(14, 'days').format('YYYY-MM-DD'))}>In 2 weeks</DropdownItem>
                        </DropdownMenu>
                     </Dropdown>
                  </label>
               </div> */}
                  </div>
                  <div className="add-note-footer">
                     <button className='btn-style-new' onClick={handleCreateNote} disabled={!activityForm['valueRichText'].trim() || loading}>
                        {loading ? 'Creating...' : 'Create Note'}
                     </button>
                  </div>
               </div>
            </div>
         </Draggable>
         {/* --------------- Altert Dropped--------------- */}
         <Modal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
            <ModalHeader toggle={() => setModalDroppedAlert(false)}></ModalHeader>
            <ModalBody>
               <div className='add-content'>
                  <h2>Dropped</h2>
                  <div className='templates-inner'>
                     <div className='btn-group'>
                        <button className='btn-style-new' onClick={() => setModalDroppedAlert(false)}>Ok</button>
                     </div>
                  </div>
               </div>
            </ModalBody>
         </Modal>
      </>

   )
}


function RemWrapper({ children, remBaseline = 16, style }) {
   const translateTransformToRem = (transform, remBaseline = 16) => {
      const convertedValues = transform
         .replace("translate(", "")
         .replace(")", "")
         .split(",")
         .map(px => px.replace("px", ""))
         .map(px => parseInt(px, 10) / remBaseline)
         .map(x => `${x}rem`);

      const [x, y] = convertedValues;
      return `translate(${x}, ${y})`;
   };

   const child = React.Children.only(children);
   const editedStyle = {
      ...child.props.style,
      ...style,
      transform: style?.transform
         ? translateTransformToRem(style.transform, remBaseline)
         : undefined,
   };

   return React.cloneElement(child, {
      ...child.props,
      style: editedStyle,
   });
}

export default AddNote;